import cn from "classnames";
import { Button } from "components/Button/Button";
import { useEffect, useRef, useState } from "react";
import { Score } from "./Score";
import { Question } from "components/Question/Question";
import { QUESTION_TYPE } from "constant";
import { AudioPlayer } from "components/AudioPlayer/AudioPlayer";

// flag for showing result before submitting;
const SHOW_RESULT = false;

export const TextTyping = ({
  text = "",
  heading = "",
  onComplete,
  audio,
  autoSubmitOnCompletion = true,
  time
}) => {
  // -----------
  // constants
  // -----------
  const [firstWord] = text.split(" ");

  // -----------
  // useStates
  // -----------
  const [currentWord, setCurrentWord] = useState(firstWord);
  const [hasError, setHasError] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(null);
  const [loading, setLoading] = useState(false)

  // -----------
  // useEffects
  // -----------
  useEffect(() => {
    if (time !== null && time === 0) {
      submitHandler();
    }
  }, [time]);

  // current word correct character count
  const [correctChars, setCorrectChars] = useState(0);

  // Will use to move the given string
  const [translateX, setTranslateX] = useState(100);

  // Speed and accuracy data
  const [accuracy, setAccuracy] = useState(0);
  const [startTime, setStartTime] = useState(null);

  // -----------
  // useRefs
  // -----------
  const inputRef = useRef(null);
  const totalCorrectChars = useRef(null);
  const wordsTyped = useRef([]);
  const wpm = useRef(0);

  // -----------
  // Functions
  // -----------
  const calculateAccuracy = () => {

    // NOTE: DO NOT REMOVE COMMENTED CODE -> LOGIC MAY NEED TO BE SWTICHED BACK AND FORTH

    // const totalChars = text.replaceAll(" ", "").length;
    // const numOfCorrectChars = totalCorrectChars.current;
    // const accuracy = Math.round((numOfCorrectChars / totalChars) * 100);

    const textWords = text.split(" ");
    const userWords = wordsTyped.current;

    let matchingWords = 0;
    let totalWords = Math.max(textWords.length, userWords.length);
    for (let i = 0; i < totalWords; i++) {
      if (textWords[i] === userWords[i]) {
        matchingWords++;
      }
    }

    const accuracy = ((matchingWords / totalWords) * 100).toFixed(2);

    return accuracy > 100 ? 100 : accuracy;
  };

  const calculateWPM = () => {
    const now = Date.now();
    const diff = (now - startTime) / 1000 / 60; // 1000 ms / 60 s

    // every word is considered to have 5 letters
    // so here we are getting all the letters in the words and divide them by 5
    // "my" shouldn't be counted as same as "deinstitutionalization"
    const totalCharsTypes = Math.ceil(
      wordsTyped.current.reduce(
        (acc, word) => (acc += word.length),
        inputValue.length ?? 0
      ) / 5
    );

    // calculating the wpm
    wpm.current = Math.ceil(totalCharsTypes / diff);
  };

  const generateTypingResult = () => {
    const accuracy = calculateAccuracy();
    const typedString = wordsTyped.current.join(" ");
    return { accuracy, wpm: wpm.current, typedString };
  };

  const handleChange = ({ target }) => {
    setTranslateX(translateX - 5);

    // Remove leading whitespace
    // Otherwise space will mark it as submit and will move to next word
    const { value } = target;
    const trimmedValue = value.trimStart();
    setInputValue(trimmedValue);

    // Only perform calculation if value has characters
    // else reset flags and pointers
    if (!trimmedValue) {
      setHasError(false);
      setCorrectChars(0);

      return;
    }

    const lastLetter = trimmedValue.slice(-1);
    setCurrentCharIndex(value.length - 1);

    // Storing intial time when first valid character typed
    // will use this for wpm calculation
    if (!startTime) {
      setStartTime(Date.now());
    }

    // Mark word submit if space and period is pressed and move to next word
    // Updated check for period case:
    // To prevent skipping if period occurs in between in last word (like emails), check last char of current word and lastLetter 
    if (lastLetter === " " || (currentWord.charAt(currentWord.length - 1) === "." && lastLetter === ".")) {
      // Updating words list
      wordsTyped.current = [...wordsTyped.current, trimmedValue.trim()];

      // Resetting flags and values
      setInputValue("");
      setCurrentCharIndex(0);
      setCorrectChars(0);

      // updating index and value to next word
      setCurrentWordIndex(currentWordIndex + 1);
      setCurrentWord(text.split(" ")[currentWordIndex + 1]);

      // typing of given text is complete
      // time to calculate speed ⚡️ and accuracy 🎯
      if (
        autoSubmitOnCompletion &&
        wordsTyped.current.length === text.split(" ").length
      ) {
        // Include period to correct Character count if present
        if (lastLetter === ".") totalCorrectChars.current += 1;

        const { typedString: answerText, ...customText } =
          generateTypingResult();

        if (!SHOW_RESULT) {
          setLoading(true)
          onComplete({ customText, answerText }, true, false, true);
        } else {
          const { accuracy } = customText;
          setAccuracy(accuracy);
        }
      }
    } else {
      // adding optional chaining in case the answer string is longer than the expected string
      if (!currentWord?.includes(trimmedValue)) {
        setHasError(true);
      } else {
        setHasError(false);
        setCorrectChars(trimmedValue.length);
        totalCorrectChars.current += 1;
      }
    }
    calculateWPM();
  };

  /**
  * Handles clearing of text input based on Backspace or Delete key presses.
  */
  const handleTextClear = (e) => {
    // Check if the pressed key is Backspace or Delete
    if (!(e.key === 'Backspace' || e.key === 'Delete')) return;

    // Check if there is any text in the input or if no words are typed
    if (e.target.value || !wordsTyped.current.length) return;

    // Check if the current word index is 0
    if (!currentWordIndex) {
      // Remove the first typed word from the array
      wordsTyped.current.pop();
      return;
    }

    // Store the current word index and the word typed by the user
    const oldWordIndex = currentWordIndex;
    const userTypedWord = wordsTyped.current[oldWordIndex - 1];

    // Get the correct text word from the original text
    const correctTextWord = text.split(" ")[oldWordIndex - 1];

    // Remove the last typed word from the array
    wordsTyped.current.pop();

    // Set input value to the user-typed word
    setInputValue(userTypedWord);

    // Update current word index to the previous word
    setCurrentWordIndex(currentWordIndex - 1);

    // Set the current word to the correct text word
    setCurrentWord(correctTextWord);

    // Set the current character index to the last character of the user-typed word
    setCurrentCharIndex(userTypedWord.length - 1);

    // Check for errors - if user-typed word does not match correct text word
    if (!correctTextWord?.includes(userTypedWord)) setHasError(true);
    else setHasError(false);
  };

  const submitHandler = () => {
    setLoading(true)
    // Including input value which might not get added because if space or period didn't get pressed
    if (!!inputValue) {
      // Updating words list
      wordsTyped.current = [...wordsTyped.current, inputValue.trim()];

      // Resetting flags and values
      setInputValue("");
      setCurrentCharIndex(0);
      setCorrectChars(0);
    }

    const { wpm, accuracy, typedString: answerText } = generateTypingResult();
    onComplete({
      customText: { wpm, accuracy },
      answerText,
    }, true, false, true);
  };

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col items-center justify-between py-7",
        "sm:py-0",
        {
          "gap-4": !audio,
          "gap-10": audio,
        }
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {audio ? (
        <div className="flex flex-col items-center justify-center gap-5 sm:gap-10">
          <Question text={heading} type={QUESTION_TYPE.TextTyping} />
          {/* <audio key={audio.mediaId} src={audio.mediaUrl} controls autoPlay /> */}
          <AudioPlayer src={audio.mediaUrl} autoPlayDelay={2} />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-10">
          <h4 className="text-center text-[26px] font-medium text-cocoa-brown">
            Type out the sentence given below
          </h4>
          <div
            className={cn(
              "flex flex-wrap gap-1 rounded-[10px] bg-[#4528120a] px-5 py-7 text-left text-xl font-medium text-[#93857B] transition-all sm:gap-2 sm:text-3xl",
              {
                "startTyping relative": currentCharIndex === null,
              }
            )}
          // style={{ translate: `${translateX}px` }}
          >
            {text.split(" ").map((word, index) => {
              const isCurrentWord =
                word === currentWord && index === currentWordIndex;
              const wordCompleted = index < currentWordIndex;
              return (
                <>
                  <p
                    className={cn({
                      "text-gray-500": wordCompleted,
                    })}
                  >
                    {hasError && isCurrentWord && (
                      // In case of error in current word,
                      // marking whole input value as error
                      <span className="text-red-500 line-through">
                        {inputValue}
                      </span>
                    )}
                    {word.split("").map((character, charIndex) => {
                      // In case of error in current word,
                      // only show character which have not marked as correct yet
                      if (
                        charIndex < correctChars &&
                        hasError &&
                        isCurrentWord
                      ) {
                        return null;
                      }

                      const isCorrrect =
                        inputValue.length &&
                        currentCharIndex >= charIndex &&
                        !hasError;

                      return (
                        <span
                          className={cn({
                            underline: isCurrentWord,
                            "text-green-500": isCorrrect && isCurrentWord,
                          })}
                        >
                          {character}
                        </span>
                      );
                    })}
                  </p>
                </>
              );
            })}
          </div>
        </div>
      )}

      {accuracy ? (
        <div className="flex h-full flex-col items-center justify-center gap-10">
          <div className="flex items-center gap-4">
            <Score label={"Words/min"} score={wpm.current} />
            <Score label={"Accuracy %"} score={accuracy} />
          </div>
          <Button
            onClick={() => {
              setLoading(true)
              onComplete({
                customText: { wpm: wpm.current, accuracy },
                answerText: wordsTyped.current.join(" "),
              }, true, false, true)
            }
            }
            className="bg-cocoa-brown text-white"
            disabled={loading}
          >
            Next
          </Button>
        </div>
      ) : (
        <div className={cn(
          "w-full flex-1",
          "sm:flex-initial",
          { "relative": !!audio },
          { "absolute top-[50%]": !audio }
        )}>
          {!!audio && (
            <>
              <p
                className={cn(
                  "inline-block h-full w-full rounded-[10px] bg-[#4528120a] px-5 py-7 text-left text-xl font-medium text-[#93857B] transition-all",
                  "sm:text-2xl",
                  {
                    "startTypingAudio relative": currentCharIndex === null,
                  }
                )}
                style={{ wordBreak: 'break-word' }}
              >
                {wordsTyped.current.length === 0 && !inputValue ? (
                  "Start typing here..."
                ) : (
                  <>
                    {wordsTyped.current.join(" ")}{" "}
                    <span className="text-black">{inputValue}</span>
                  </>
                )}
              </p>
            </>
          )}
          <input
            ref={inputRef}
            className={cn(
              "absolute -top-full -left-full flex-1 border-gray-700 bg-transparent text-2xl caret-transparent outline-none"
              // { "h-full w-full text-center": !audio, "h-0 w-0": audio }
            )}
            type="text"
            autoCapitalize="off"
            onChange={handleChange}
            onKeyUp={handleTextClear}
            value={inputValue}
            autoFocus={true}
            placeholder={
              currentCharIndex === null && currentWordIndex === 0
                ? "Start typing"
                : null
            }
          />
        </div>
      )}
      {!!audio && (
        <div className="text-center">
          <Button
            disabled={(wordsTyped.current.length === 0 && !inputValue) || loading}
            onClick={submitHandler}
            className="bg-cocoa-brown text-white"
          >
            Submit
          </Button>
        </div>
      )}
    </div>
  );
};
